// ck-gt-product-bundle: CK-only subclass of <product-bundle>.
// Counts by total units (not slots), merges re-added products into one slot,
// and applies tiered discount codes by unit count (apply via /discount/ link, remove via cart API).
// The shared ProductBundle class and the default <product-bundle> section are left untouched.

(function () {
  if (typeof customElements === 'undefined' || !customElements.whenDefined) return;

  customElements.whenDefined('product-bundle').then((ProductBundle) => {
    if (customElements.get('ck-gt-product-bundle')) return;

    class CkGtProductBundle extends ProductBundle {
      constructor() {
        super();
        this._ckSyncing = false;
        this._ckSyncPending = false;
        this._ckSuppressSync = false;
        this._ckSyncDiscount = theme.utils.debounce(this.ckSyncDiscount.bind(this), 400);
      }

      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();
        // Pre-cache the slot template while empty slots still exist, so adding more
        // distinct products than the initial slots can clone a new row without error
        // (the base template getter would otherwise clone from a missing available slot).
        try { this.template; } catch (e) {}
      }

      // Clearing the builder after add-to-cart must NOT touch the cart's codes — the
      // earned tier code should stay applied to the bundle that was just added.
      resetVariants() {
        this._ckSuppressSync = true;
        super.resetVariants();
        // Release after the sync debounce window so any sync scheduled by the
        // clearVariant calls above is skipped.
        setTimeout(() => { this._ckSuppressSync = false; }, 600);
      }

      get tiers() {
        if (this._ckTiers) return this._ckTiers;
        let parsed = [];
        try {
          const raw = this.getAttribute('data-tiers');
          parsed = raw ? JSON.parse(raw) : [];
        } catch (e) {
          parsed = [];
        }
        this._ckTiers = parsed
          .map((t) => ({ min: parseInt(t.min), code: (t.code || '').trim(), percent: parseFloat(t.percent) || 0 }))
          .filter((t) => t.code && !isNaN(t.min))
          .sort((a, b) => a.min - b.min);
        return this._ckTiers;
      }

      get tierCodes() {
        return this.tiers.map((t) => t.code);
      }

      get wrapper() {
        return this.closest('.product-bundle-wrapper');
      }

      // Only filled slots are real bundle variants. Empty placeholders have no
      // variant id, so they must never reach the cart payload (would be
      // "variant not found") nor be validated/counted.
      get variants() {
        return super.variants.filter((variant) => !variant.hasAttribute('available'));
      }

      // Total units = sum of quantity inputs across filled slots.
      unitCount() {
        let count = 0;
        this.variants.forEach((variant) => {
          if (variant.hasAttribute('available')) return;
          const qty = variant.querySelector('quantity-input');
          const value = qty ? parseInt(qty.value) : 0;
          if (!isNaN(value)) count += value;
        });
        return count;
      }

      // Submit gate (units), product-grid lock (units vs "Maximum items"),
      // and per-slot "+" availability (per-product "Maximum" only).
      ckUpdateGating() {
        const count = this.unitCount();

        if (this.submitButton) {
          this.submitButton.disabled = count < this.bundleMin;
        }

        // Bundle max comes from the last tier — lock the grid (adding products) at the
        // max, not quantity. Only meaningful when tiers are configured.
        const wrapper = this.wrapper;
        if (wrapper) {
          if (this.tiers.length && count >= this.bundleMax) wrapper.setAttribute('locked', '');
          else wrapper.removeAttribute('locked');
        }

        // A "+" is only capped by the per-product "Maximum", which Liquid sets as the
        // input's max attribute solely when "Set maximum quantity" is checked. With the
        // checkbox off there is no max attribute, so "+" stays enabled and quantity is free.
        this.variants.forEach((variant) => {
          if (variant.hasAttribute('available')) return;
          const qty = variant.querySelector('quantity-input');
          if (!qty) return;
          const plus = qty.querySelector('[name="plus"]');
          const input = qty.querySelector('input');
          if (!plus || !input) return;
          const maxAttr = input.getAttribute('max');
          const max = maxAttr != null && maxAttr !== '' ? parseInt(maxAttr) : NaN;
          const value = parseInt(input.value);
          plus.disabled = !isNaN(max) && !isNaN(value) && value >= max;
        });
      }

      addVariant(product, variant) {
        const existing = this.variants.find(
          (slot) =>
            !slot.hasAttribute('available') &&
            slot.getAttribute('data-variant-id') == variant.id
        );

        if (existing) {
          // Merge: bump the existing slot's quantity instead of adding a new line.
          const plus = existing.querySelector('quantity-input [name="plus"]');
          if (plus && !plus.disabled) plus.click();
        } else {
          super.addVariant(product, variant);
        }

        this.ckUpdateGating();
        this._ckSyncDiscount();
      }

      clearVariant(variant) {
        super.clearVariant(variant);
        this.ckUpdateGating();
        this._ckSyncDiscount();
      }

      // Lowest tier still ahead of the current count (the next one to unlock), or null.
      ckNextTier() {
        const count = this.unitCount();
        for (const tier of this.tiers) {
          if (tier.min > count) return tier;
        }
        return null;
      }

      // Threshold of the highest tier already reached (0 if none).
      ckCurrentTierMin() {
        const count = this.unitCount();
        let min = 0;
        for (const tier of this.tiers) {
          if (count >= tier.min) min = tier.min;
        }
        return min;
      }

      updateProgressBar() {
        const element = this.querySelector('[data-product-bundle-progress-bar]');
        if (!element) return;
        const count = this.unitCount();
        let percent;
        if (!this.tiers.length) {
          percent = this.bundleMin > 0 ? (100 * count) / this.bundleMin : 0;
        } else {
          const next = this.ckNextTier();
          if (!next) {
            percent = 100; // highest tier reached
          } else {
            // Segmented: fill from the current tier to the next, resetting at each tier.
            const prevMin = this.ckCurrentTierMin();
            const span = next.min - prevMin;
            percent = span > 0 ? (100 * (count - prevMin)) / span : 0;
          }
        }
        if (percent < 0) percent = 0;
        if (percent > 100) percent = 100;
        element.style.setProperty('--progress', `${percent}%`);
      }

      // Discount % of the highest reached tier (0 = none).
      ckActivePercent() {
        const count = this.unitCount();
        let percent = 0;
        for (const tier of this.tiers) {
          if (count >= tier.min) percent = tier.percent || 0;
        }
        return percent;
      }

      ckSubtotal() {
        let subtotal = 0;
        this.variants.forEach((variant) => {
          if (variant.hasAttribute('available')) return;
          const price = variant.querySelector('[data-product-bundle-variant-price]');
          const qty = variant.querySelector('quantity-input');
          if (!price || !qty) return;
          const cents = parseInt(price.getAttribute('data-price'));
          const value = parseInt(qty.value);
          if (!isNaN(cents) && !isNaN(value)) subtotal += cents * value;
        });
        return subtotal;
      }

      // Render a total element, showing the active tier's discount as a struck-through
      // original price plus the discounted price (display-only estimate).
      ckRenderTotal(element, format) {
        if (!element) return;
        const subtotal = this.ckSubtotal();
        const percent = this.ckActivePercent();
        if (percent > 0 && subtotal > 0) {
          const discounted = Math.round((subtotal * (100 - percent)) / 100);
          element.innerHTML =
            '<span class="ck-gt-bundle-total__discounted">' +
            theme.Currency.formatMoney(discounted, format) +
            '</span><s class="ck-gt-bundle-total__original">' +
            theme.Currency.formatMoney(subtotal, format) +
            '</s>';
        } else {
          element.innerHTML = theme.Currency.formatMoney(subtotal, format);
        }
      }

      updateTotal() {
        this.ckRenderTotal(this.querySelector('[data-product-bundle-total]'), theme.settings.moneyFormat);
      }

      updateTotalWithCurrency() {
        this.ckRenderTotal(
          this.querySelector('[data-product-bundle-total-with-currency]'),
          theme.settings.moneyWithCurrencyFormat
        );
      }

      onChangeHandler() {
        super.onChangeHandler();
        this.ckUpdateGating();
        this._ckSyncDiscount();
      }

      ckCartSections() {
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
        );
        return sections;
      }

      async ckGetCart() {
        return (await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json());
      }

      async ckSyncDiscount() {
        if (this._ckSuppressSync) return;
        if (!this.tiers.length) return;

        if (this._ckSyncing) {
          this._ckSyncPending = true;
          return;
        }
        this._ckSyncing = true;
        try {
          do {
            this._ckSyncPending = false;
            await this.ckSyncDiscountOnce();
          } while (this._ckSyncPending);
        } finally {
          this._ckSyncing = false;
        }
      }

      async ckSyncDiscountOnce() {
        const tierCodes = this.tierCodes;

        const count = this.unitCount();
        let desired = null;
        for (const tier of this.tiers) {
          if (count >= tier.min) desired = tier.code;
        }

        let cart;
        try {
          cart = await this.ckGetCart();
        } catch (e) {
          return;
        }

        // Compare codes case-insensitively (Shopify may return a different case than
        // what's typed in the settings). Other (non-tier) codes keep their cart casing.
        const current = (cart.discount_codes || []).map((d) => d.code);
        const tierCodesLC = tierCodes.map((c) => c.toLowerCase());
        const desiredLC = desired ? desired.toLowerCase() : null;
        const isTier = (c) => tierCodesLC.indexOf(c.toLowerCase()) !== -1;

        const preserved = current.filter((c) => !isTier(c));
        const stale = current.filter((c) => isTier(c) && c.toLowerCase() !== desiredLC);

        const needApply = desired && current.every((c) => c.toLowerCase() !== desiredLC);
        const needRemove = stale.length > 0;

        if (!needApply && !needRemove) return;

        // Apply the active tier code through the Shopify discount link (preserves other codes).
        if (needApply) {
          try {
            await fetch(`/discount/${encodeURIComponent(desired)}?redirect=/cart.js`, { method: 'GET' });
          } catch (e) {
            // ignore — fall through to refresh
          }
        }

        let latest = cart;

        if (needRemove) {
          // Cart API is the only way to drop a code: send preserved + active tier code.
          const finalList = preserved.concat(desired ? [desired] : []);
          try {
            const body = JSON.stringify({
              discount: finalList.join(','),
              sections: this.ckCartSections(),
            });
            latest = await (await fetch(theme.routes.cart_update_url, {
              ...theme.utils.fetchConfig('json'),
              body,
            })).json();
          } catch (e) {
            return;
          }
        } else if (needApply) {
          try {
            latest = await this.ckGetCart();
          } catch (e) {
            return;
          }
        }

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'ck-gt-bundle-discount',
          cart: latest,
        });
      }
    }

    customElements.define('ck-gt-product-bundle', CkGtProductBundle);
  });
})();
